import { useMutation, useQueryClient } from '@tanstack/react-query';

import { type Notification, readAllNotifications } from '@enterprise/api';
import { ensureError, isDefined, isNotDefined } from '@enterprise/core';
import type { OperationResultWithData } from '@enterprise/types';

import { notificationsQueryKeys } from '@feat/notifications/common/notifications-query-keys';
import { useApplicationStore } from '@hooks/application/application-store.hook';
import { useNotifier } from '@hooks/notifier/notifier.hook';

import type { NotificationsCacheContext } from './types/notifications-cache-context.type';

export const useReadAllNotifications = () => {
  const enterpriseApi = useApplicationStore(({ enterpriseApi }) => {
    return enterpriseApi;
  });

  const queryClient = useQueryClient();
  const notifier = useNotifier();
  const listKey = notificationsQueryKeys.list();

  return useMutation<unknown, Error, void, NotificationsCacheContext>({
    mutationFn: () => {
      return readAllNotifications(enterpriseApi);
    },
    onError: (e, _vars, context) => {
      if (isDefined(context)) {
        queryClient.setQueryData(listKey, context.snapshot);
      }

      notifier.error({ description: ensureError(e).message, title: 'Failed to mark all as read' });
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: listKey });
      const snapshot = queryClient.getQueryData<OperationResultWithData<Notification[]>>(listKey);

      queryClient.setQueryData<OperationResultWithData<Notification[]>>(listKey, (cached) => {
        if (isNotDefined(cached)) {
          return cached;
        }

        if (cached.failure) {
          return cached;
        }

        const { data } = cached;

        if (isNotDefined(data)) {
          return cached;
        }

        const readDate = new Date().toISOString();
        const updated = data.map((item) => {
          if (isNotDefined(item.readDate)) {
            return { ...item, readDate };
          }

          return item;
        });

        return { ...cached, data: updated };
      });

      return { snapshot };
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: listKey });
    },
  });
};
